import React, { useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import { CheckCircle, ArrowForward, Send } from "@mui/icons-material";

const GoogleSheetsService = {
  async saveUserData(userData) {
    try {
      const params = {
        action: "saveUserData",
        fullName: userData.fullName || "",
        email: userData.email || "",
        companyWebsite: userData.companyWebsite || "",
        phoneNumber: userData.phoneNumber || "",
        assistantName: userData.assistantName || "Unknown Assistant",
        pageUrl: typeof window !== "undefined" ? window.location.href : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        referrer:
          typeof document !== "undefined" ? document.referrer || "" : "",
        timestamp: new Date().toISOString(),
      };

      const result = await jsonp(GAS_URL, params, 20000); // 20 second timeout

      // Check if the response indicates success
      if (result && result.success === false) {
        throw new Error(result.error || result.message || "Submission failed");
      }

      // If no explicit success field, assume success if we got a response
      if (result && typeof result === "object") {
        return {
          success: true,
          ...result,
        };
      }

      return result;
    } catch (error) {
      console.error("Google Sheets service error:", error);
      throw error;
    }
  },

  async testConnection() {
    try {
      console.log("Testing Google Sheets connection...");
      const result = await jsonp(GAS_URL, { action: "ping" }, 10000);
      console.log("Connection test result:", result);
      return result;
    } catch (error) {
      console.error("Connection test failed:", error);
      throw error;
    }
  },

  // Helper method to check if the service is working
  async isServiceAvailable() {
    try {
      const result = await this.testConnection();
      return result && (result.success === true || result.message);
    } catch (error) {
      return false;
    }
  },
};

function jsonp(url, params = {}, timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    const cbName = "cb_" + Math.random().toString(36).slice(2);
    const script = document.createElement("script");
    let completed = false;

    const timeout = setTimeout(() => {
      if (!completed) {
        cleanup();
        reject(new Error("Request timeout - Google Apps Script may be slow"));
      }
    }, timeoutMs);

    function cleanup() {
      completed = true;
      clearTimeout(timeout);
      if (window[cbName]) {
        delete window[cbName];
      }
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    }

    // Set up the callback function
    window[cbName] = (data) => {
      if (!completed) {
        cleanup();
        resolve(data);
      }
    };

    // Build query string
    const qs = new URLSearchParams({ ...params, callback: cbName }).toString();
    const finalUrl = `${url}?${qs}`;

    script.src = finalUrl;

    script.onerror = (error) => {
      if (!completed) {
        cleanup();
        reject(
          new Error(
            "Failed to load Google Apps Script - check deployment settings"
          )
        );
      }
    };

    script.onload = () => {
      console.log("Script loaded successfully");
    };

    // Add script to document
    document.head.appendChild(script);
  });
}

const GAS_URL =
  "https://script.google.com/macros/s/AKfycby1yoYypto9IAbJv_kHuL6pXxEzgJlwzjOPPt0C6hwoG8kThcx1DzNu0i2AE7EB7F8XYg/exec";

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    companyWebsite: "",
    agreeToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");

  // List of personal email domains to restrict
  const personalEmailDomains = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "aol.com",
    "icloud.com",
    "protonmail.com",
    "mail.com",
    "yandex.com",
    "zoho.com",
  ];

  const validateEmail = (email) => {
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }

    // Extract domain from email
    const domain = email.split("@")[1]?.toLowerCase();

    // Check if it's a personal email domain
    if (personalEmailDomains.includes(domain)) {
      return "Please use your company email address instead of personal email";
    }

    return "";
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Validate email on change
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.fullName || !formData.email || !formData.agreeToTerms) {
      alert("Please fill in all required fields and agree to terms");
      return;
    }

    // Validate email before submission
    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // First, save to Google Sheets using JSONP (this should work without CORS issues)
      console.log("Saving to Google Sheets...");
      const sheetResult = await GoogleSheetsService.saveUserData({
        fullName: formData.fullName,
        email: formData.email,
        companyWebsite: formData.companyWebsite,
        assistantName: "Voice Agent Demo",
      });
      console.log("Google Sheets save result:", sheetResult);

      // If Google Sheets save was successful, try the main API
      if (sheetResult && sheetResult.success) {
        try {
          // Prepare data for API call
          const apiData = {
            user_name: formData.fullName,
            user_email: formData.email,
            url: formData.companyWebsite || "",
          };

          console.log("Sending data to main API:", apiData);

          // Call the main API
          const response = await fetch(
            "https://api.crawler.beanbag.ai/api/create-agent",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify(apiData),
            }
          );

          console.log("API Response status:", response.status);

          if (!response.ok) {
            const errorText = await response.text();
            console.error("API Error response:", errorText);
            // Don't throw here - we already saved to sheets successfully
            console.warn(
              `Main API failed (${response.status}), but data was saved to Google Sheets`
            );
          } else {
            const result = await response.json();
            console.log("Main API call successful:", result);
          }
        } catch (apiError) {
          console.error("Main API call failed (non-critical):", apiError);
          // Don't fail the whole process if main API fails - sheets save was successful
        }

        setIsSubmitted(true);
      } else {
        throw new Error("Failed to save data to Google Sheets");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(`Failed to submit form: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "350px",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #4caf50, #45a049)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1rem",
          }}
        >
          <CheckCircle sx={{ color: "white", fontSize: "40px" }} />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: "#333",
            fontWeight: "600",
            marginBottom: "0.5rem",
          }}
        >
          Thank You!
        </Typography>
        <Typography
          sx={{
            color: "#666",
            fontSize: "0.9rem",
          }}
        >
          Awesome! Your Voice Agent will be in your inbox soon!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#333",
          fontWeight: "600",
          marginBottom: "1.5rem",
          textAlign: "center",
        }}
      >
        Share Your Details
      </Typography>

      <form
        onSubmit={handleSubmit}
        style={{ width: "100%", maxWidth: "400px" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <TextField
            name="fullName"
            label="Full Name"
            value={formData.fullName}
            onChange={handleInputChange}
            required
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#ea6347",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ea6347",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#ea6347",
              },
            }}
          />

          <TextField
            name="email"
            label="Company Email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            fullWidth
            size="small"
            error={!!emailError}
            helperText={emailError || "Please use your company email address"}
            placeholder="name@company.com"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: emailError ? "#d32f2f" : "#ea6347",
                },
                "&.Mui-focused fieldset": {
                  borderColor: emailError ? "#d32f2f" : "#ea6347",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: emailError ? "#d32f2f" : "#ea6347",
              },
              "& .MuiFormHelperText-root": {
                color: emailError ? "#d32f2f" : "#666",
                fontSize: "0.75rem",
              },
            }}
          />

          <TextField
            name="companyWebsite"
            label="Company Website URL"
            value={formData.companyWebsite}
            onChange={handleInputChange}
            fullWidth
            size="small"
            placeholder="https://example.com"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#ea6347",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ea6347",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#ea6347",
              },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
                sx={{
                  color: "#ea6347",
                  "&.Mui-checked": {
                    color: "#ea6347",
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.875rem", color: "#666" }}>
                I agree to receive my Custom AI agent
              </Typography>
            }
            sx={{ alignSelf: "flex-start" }}
          />

          <Button
            disabled={isSubmitting || !!emailError}
            type="submit"
            variant="contained"
            sx={{
              mt: { xs: 1, sm: 2 },
              background: "#e95f43",
              color: "#fff",
              borderRadius: "20px",
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
              textTransform: "none",
              "&:hover": {
                background: "#d54e36",
              },
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
